import React, { useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import DisabledProcoreLogo from 'icons/procore-logo-disabled.svg';
import ProcoreLogo from 'icons/procore-logo-white.svg';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { Trans, useTranslation } from 'react-i18next';

import { useDestroyProcoreIdentityMutation } from 'components/hooks/procoreIdentityMutations';

import OutlinedButton from 'components/application/buttons/OutlinedButton';
import Glimmer from 'components/application/Glimmer';

import { useCurrentActor } from 'components/contexts/CurrentActor';
import CardWrapper from 'components/procore/fileDestination/components/cards/CardWrapper';
import UnlinkProcoreIntegrationModal from 'components/settings/externalIntegrationConfiguration/components/UnlinkProcoreIntegrationModal';

import { sendAnalytics } from 'components/helpers/analytics';
import { formatDate } from 'components/helpers/dates';
import { resourceShape } from 'components/helpers/serialisableResources';
import { personDisplayName } from 'components/helpers/users';

export default function ProcoreIntegrationConfiguration({
  isProcoreSession,
  missingProcoreAccountRequirement,
  procoreAccountIntegration,
  procoreProjectIntegrationCount,
  seedProcoreIdentity,
}) {
  const destroyProcoreIdentityMutation = useDestroyProcoreIdentityMutation();

  const [isUnlinkModalOpen, setIsUnlinkModalOpen] = useState(false);

  const { t: i18nT } = useTranslation('components', {
    keyPrefix:
      'settings.externalIntegrationConfiguration.components.ProcoreIntegrationConfiguration',
  });

  const { t: i18nTCommon } = useTranslation('common');

  // query function not required, provided initial data, then data updated via mutation
  const { data: domainProcoreIdentity } = useQuery({
    queryKey: ['integrations', 'procoreIdentity'],
    initialData: seedProcoreIdentity,
  });

  const {
    data: domainProcoreUser,
    isSuccess: isDomainProcoreUserFetchSuccess,
  } = useQuery({
    queryKey: ['procore', 'user'],
    queryFn: async () => {
      const response = await axios.get('/procore/user');

      return response.data;
    },
    placeholderData: { data: null, meta: {} },
    enabled: !!domainProcoreIdentity.data,
    refetchInterval: (data) =>
      !data || ['completed', 'failed'].includes(data.meta.requestStatus) ?
        false
      : 1000,
    refetchIntervalInBackground: true,
  });

  const procoreAccountIntegrationUser =
    procoreAccountIntegration.included?.find(
      (inclusion) =>
        inclusion.type === 'user' &&
        inclusion.id ===
          procoreAccountIntegration.data.relationships.user.data.id,
    );

  const isProcoreUserFetchRequestCompleted =
    isDomainProcoreUserFetchSuccess &&
    domainProcoreUser.meta.requestStatus === 'completed';

  const isProcoreUserFetchRequestFailed =
    isDomainProcoreUserFetchSuccess &&
    domainProcoreUser.meta.requestStatus === 'failed';

  const handleUnlink = () => {
    destroyProcoreIdentityMutation.mutate({
      id: domainProcoreIdentity.data.id,
    });
    setIsUnlinkModalOpen(false);
  };

  return (
    <div>
      <h3 className='tw-mb-2 tw-mt-0 tw-text-m tw-font-semibold tw-tracking-auto'>
        Procore
      </h3>
      {procoreAccountIntegration.data && (
        <p className='tw-mb-4'>
          {i18nT('accountIntegrationText', {
            date: formatDate({
              date: procoreAccountIntegration.data.attributes.createdAt,
              format: 'dd MMM yyyy',
            }),
            displayName: personDisplayName({
              attributes: procoreAccountIntegrationUser.attributes,
            }),
            interpolation: { escapeValue: false },
          })}
        </p>
      )}
      {domainProcoreIdentity.data ?
        <>
          {!procoreAccountIntegration.data && (
            <p className='tw-mb-4'>
              {i18nT(
                domainProcoreUser?.data ?
                  'userIntegrationNoProcoreAccountIntegration'
                : 'adminAccessRequiredText',
              )}
            </p>
          )}
          <CardWrapper isInactive={false}>
            {isProcoreUserFetchRequestCompleted ?
              <>
                <div className='tw-mb-1 tw-font-medium tw-text-grey-900'>
                  {isProcoreUserFetchRequestCompleted &&
                    domainProcoreUser.data?.attributes.login}
                </div>
                <p className='tw-mb-4 tw-text-s tw-font-normal tw-tracking-wide'>
                  {i18nT('userCard.text', {
                    date:
                      domainProcoreIdentity.data &&
                      formatDate({
                        date: domainProcoreIdentity.data.attributes.createdAt,
                        format: 'dd MMM yyyy',
                      }),
                  })}
                </p>
                <OutlinedButton
                  color='red'
                  onClick={() => {
                    setIsUnlinkModalOpen(true);
                  }}
                  size='sm'
                >
                  {i18nTCommon('shared.unlink')}
                </OutlinedButton>
              </>
            : isProcoreUserFetchRequestFailed ?
              <>
                <Trans
                  components={{
                    div: (
                      <div className='tw-mb-1 tw-font-medium tw-text-red-600' />
                    ),
                    p: (
                      <p className='tw-mb-4 tw-text-s tw-font-normal tw-tracking-wide' />
                    ),
                    a: (
                      /* eslint-disable jsx-a11y/anchor-has-content */
                      <a
                        className='app-link va-bottom tw-text-s tw-font-medium tw-tracking-wide'
                        id='intercom-launcher'
                      />
                      /* eslint-enable jsx-a11y/anchor-has-content */
                    ),
                  }}
                  i18nKey={`settings.externalIntegrationConfiguration.components.ProcoreIntegrationConfiguration.${domainProcoreUser.meta.errorReason === 'not_found' ? 'procoreUserNotFoundError' : 'procoreUserUnknownError'}`}
                  ns='components'
                />
                <OutlinedButton
                  color='red'
                  onClick={() => {
                    setIsUnlinkModalOpen(true);
                  }}
                  size='sm'
                >
                  {i18nTCommon('shared.disconnect')}
                </OutlinedButton>
              </>
            : <>
                <div className='tw-text-grey-900'>
                  {i18nTCommon('integrations.integrationSetUp', {
                    provider: 'Procore',
                  })}
                </div>
                <div className='tw-text-s tw-font-normal tw-tracking-wide'>
                  <span>
                    {i18nTCommon('shared.loading...')} <Glimmer />
                  </span>
                </div>
              </>
            }
          </CardWrapper>
        </>
      : procoreAccountIntegration.data && !domainProcoreUser?.data ?
        <IntegrationButton
          isProcoreSession={isProcoreSession}
          text={i18nTCommon('integrations.connectMyAccount')}
        />
      : (
        !procoreAccountIntegration.data &&
        !domainProcoreUser?.data &&
        missingProcoreAccountRequirement
      ) ?
        <>
          <p className='tw-mb-4'>
            {i18nT(
              `noProcoreAccountIntegrationWithMissingRequirement.${_.camelCase(missingProcoreAccountRequirement)}`,
            )}
          </p>
          <div>
            <p className='tw-mb-4' />
            <button
              className='tw-apply-filled-button tw-apply-filled-button--on-light tw-apply-filled-button--md tw-apply-filled-button--procore'
              disabled={true}
            >
              <DisabledProcoreLogo className='tw-mr-4.5' />
              {i18nTCommon('integrations.integrateWith', {
                provider: 'Procore',
              })}
            </button>
          </div>
        </>
      : (
        !procoreAccountIntegration.data &&
        !domainProcoreUser?.data &&
        !missingProcoreAccountRequirement
      ) ?
        <>
          <p className='tw-mb-4'>{i18nT('adminAccessRequiredText')}</p>
          <IntegrationButton
            isProcoreSession={isProcoreSession}
            text={i18nTCommon('integrations.integrateWith', {
              provider: 'Procore',
            })}
          />
        </>
      : null}
      {isUnlinkModalOpen && (
        <UnlinkProcoreIntegrationModal
          isUnlinking={destroyProcoreIdentityMutation.isLoading}
          onClose={() => {
            setIsUnlinkModalOpen(false);
          }}
          onUnlink={handleUnlink}
          procoreProjectIntegrationCount={procoreProjectIntegrationCount}
        />
      )}
    </div>
  );
}

ProcoreIntegrationConfiguration.propTypes = {
  missingProcoreAccountRequirement: PropTypes.string,
  procoreProjectIntegrationCount: PropTypes.number.isRequired,
  procoreAccountIntegration: PropTypes.shape({
    data: resourceShape('procoreAccountIntegration'),
  }),
  seedProcoreIdentity: PropTypes.shape({
    data: resourceShape('procoreIdentity'),
  }),
  isProcoreSession: PropTypes.bool,
};

function IntegrationButton({ isProcoreSession, text }) {
  const { user: currentUser } = useCurrentActor();

  if (isProcoreSession) {
    return (
      <a
        className='tw-apply-filled-button tw-apply-filled-button--on-light tw-apply-filled-button--md tw-apply-filled-button--procore'
        href='/integrations/iframe_journeys/new'
        onClick={() =>
          sendAnalytics(
            'Integrate with Procore clicked from within embedded app',
            { currentUser },
          )
        }
      >
        <ProcoreLogo className='tw-mr-4.5' />
        {text}
      </a>
    );
  } else {
    return (
      <a
        className='tw-apply-filled-button tw-apply-filled-button--on-light tw-apply-filled-button--md tw-apply-filled-button--procore'
        data-method='post'
        href='/users/auth/procore'
        onClick={() =>
          sendAnalytics(
            'Integrate with Procore clicked from outside embedded app',
            { currentUser },
          )
        }
        rel='nofollow'
      >
        <ProcoreLogo className='tw-mr-4.5' />
        {text}
      </a>
    );
  }
}

IntegrationButton.propTypes = {
  isProcoreSession: PropTypes.bool,
  text: PropTypes.string.isRequired,
};
